import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Anchor, Badge, Button, Checkbox, Container, Drawer, Group, Modal, Pagination, Paper, Select, Stack,
  Switch, Table, Tabs, Text, Textarea, Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconArrowBackUp, IconBan, IconCheck, IconFileTypePdf, IconPencil, IconPlus } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { IssueRecipient, StockIssue, StockIssueChange, StockIssueKind } from "../api/types";
import { HoldingsTab } from "../components/freeIssues/HoldingsTab";
import { IssueForm, RecipientBadge, type IssueFormMode } from "../components/freeIssues/IssueForm";
import { TechniciansTab } from "../components/freeIssues/TechniciansTab";
import { fmtQty, isWholeUnit } from "../components/freeIssues/qty";
import { useCurrentUser } from "../hooks/useCurrentUser";

const PAGE = 50;
const recKey = (r: { type: string; id: number }) => `${r.type}:${r.id}`;

function fmtDate(s: string | null) {
  return s ? new Date(s).toLocaleString() : "—";
}

function KindBadge({ issue }: { issue: StockIssue }) {
  const { t } = useTranslation();
  const color = issue.kind === "issue" ? "blue" : issue.kind === "return" ? "teal" : "gray";
  return (
    <Badge size="sm" variant="light" color={color}>
      {t(`freeIssues.kind.${issue.kind}`)}
      {issue.kind === "close" && issue.close_kind ? ` · ${t(`freeIssues.closeKind.${issue.close_kind}`)}` : ""}
    </Badge>
  );
}

function AccountingBadge({ issue }: { issue: StockIssue }) {
  const { t } = useTranslation();
  if (!issue.accounting_required) return <Text size="sm" c="dimmed">—</Text>;
  return issue.accounting_confirmed_at ? (
    <Badge size="sm" color="green" variant="light">{t("freeIssues.accConfirmed")}</Badge>
  ) : (
    <Badge size="sm" color="orange" variant="light">{t("freeIssues.accPending")}</Badge>
  );
}

/** Badge-uri „Anulată” / „Modificată”. */
function StatusBadges({ issue }: { issue: StockIssue }) {
  const { t } = useTranslation();
  if (issue.cancelled_at) return <Badge size="sm" color="red" variant="filled">{t("freeIssues.cancelledBadge")}</Badge>;
  if (issue.revision > 1) return <Badge size="sm" color="yellow" variant="light">{t("freeIssues.editedBadge")}</Badge>;
  return null;
}

const accPending = (iss: StockIssue) => iss.accounting_required && !iss.accounting_confirmed_at && !iss.cancelled_at;
/** Modifică / Anulează: doar predările neanulate. */
const editable = (iss: StockIssue) => iss.kind === "issue" && !iss.cancelled_at;

/** „+2 Power Supply 24v, −1 Antena…” — u.m. doar dacă nu e `buc`. */
function changesText(changes: StockIssueChange[]): string {
  return changes
    .map((c) => {
      const d = Number(c.delta);
      const unit = c.unit && !isWholeUnit(c.unit) ? ` ${c.unit}` : "";
      return `${d > 0 ? "+" : "−"}${fmtQty(Math.abs(d))}${unit} ${c.name}`;
    })
    .join(", ");
}

type OverRow = { stock_item_id: number; name: string; requested: number; holding: number };

export default function FreeIssues() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";
  const isMobile = useMediaQuery("(max-width: 48em)");

  const [rows, setRows] = useState<StockIssue[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState<StockIssueKind | null>(null);
  const [recipient, setRecipient] = useState<IssueRecipient | null>(null);
  const [recOptions, setRecOptions] = useState<IssueRecipient[]>([]);
  const [recSearch, setRecSearch] = useState("");
  const [selected, setSelected] = useState<StockIssue | null>(null);
  const [form, setForm] = useState<
    { mode: IssueFormMode; recipient: IssueRecipient | null; issue?: StockIssue } | null
  >(null);
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [cancelledOnly, setCancelledOnly] = useState(false);
  const [cancelling, setCancelling] = useState<StockIssue | null>(null);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelOver, setCancelOver] = useState<OverRow[]>([]);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [accBusy, setAccBusy] = useState(false);
  const [holdingsKey, setHoldingsKey] = useState(0);
  const [tab, setTab] = useState<string | null>("ops");
  const [searchParams, setSearchParams] = useSearchParams();
  const deepId = Number(searchParams.get("id")) || null;
  const loadSeq = useRef(0);
  const searchSeq = useRef(0);

  async function load() {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const r = await api.listStockIssues({
        kind,
        technician_id: recipient?.type === "technician" ? recipient.id : null,
        customer_id: recipient?.type === "customer" ? recipient.id : null,
        unconfirmed,
        cancelled: cancelledOnly ? true : null,
        limit: PAGE,
        offset: (page - 1) * PAGE,
      });
      if (seq !== loadSeq.current) return;
      setRows(r.items);
      setTotal(r.total);
      // Selecția rămâne doar pe ce e încă vizibil și neconfirmat.
      setChecked((c) => new Set(r.items.filter((i) => c.has(i.id) && accPending(i)).map((i) => i.id)));
    } catch (e) {
      notifications.show({ color: "red", message: e instanceof ApiError ? t("freeIssues.loadFailed") : String(e) });
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [kind, recipient, page, unconfirmed, cancelledOnly]);

  // Deep link /free-issues?id=<id> (Telegram / notificări): deschide drawer-ul.
  useEffect(() => {
    if (deepId == null) return;
    if (selected?.id === deepId) return;
    let cancelled = false;
    api.getStockIssue(deepId)
      .then((r) => { if (!cancelled) setSelected(r.issue); })
      .catch(() => {
        if (cancelled) return;
        notifications.show({ color: "red", message: t("freeIssues.notFound") });
        clearDeepLink();
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepId]);

  function clearDeepLink() {
    if (!searchParams.has("id")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("id");
    setSearchParams(next, { replace: true });
  }

  function closeDrawer() {
    setSelected(null);
    clearDeepLink();
  }

  function replaceIssue(iss: StockIssue) {
    setRows((rs) => rs.map((r) => (r.id === iss.id ? iss : r)));
    setSelected((s) => (s?.id === iss.id ? iss : s));
  }

  async function accountingAction(iss: StockIssue, confirm: boolean) {
    setAccBusy(true);
    try {
      const r = confirm ? await api.confirmIssueAccounting(iss.id) : await api.undoIssueAccounting(iss.id);
      replaceIssue(r.issue);
      setChecked((c) => { const n = new Set(c); n.delete(iss.id); return n; });
      if (unconfirmed) void load();
    } catch (e) {
      notifications.show({ color: "red", title: t("common.operationFailed"), message: apiErr(e) });
    } finally {
      setAccBusy(false);
    }
  }

  async function confirmSelected() {
    if (checked.size === 0) return;
    setAccBusy(true);
    try {
      const r = await api.confirmIssueAccountingBulk([...checked]);
      notifications.show({ color: "green", message: t("freeIssues.accBulkDone", { count: r.changed }) });
      setChecked(new Set());
      void load();
    } catch (e) {
      notifications.show({ color: "red", title: t("common.operationFailed"), message: apiErr(e) });
    } finally {
      setAccBusy(false);
    }
  }

  /** Reîncarcă predarea înainte de formular, ca limitele să folosească datele curente. */
  async function openEdit(iss: StockIssue) {
    try {
      const r = await api.getStockIssue(iss.id);
      replaceIssue(r.issue);
      if (!editable(r.issue)) {
        notifications.show({ color: "red", message: t("freeIssues.errors.not_editable") });
        return;
      }
      setForm({ mode: "edit", recipient: null, issue: r.issue });
    } catch (e) {
      notifications.show({ color: "red", title: t("common.operationFailed"), message: apiErr(e) });
    }
  }

  /** `not_editable`: închide formularul/modalul și reîmprospătează predarea. */
  async function onNotEditable(id: number) {
    setForm(null);
    setCancelling(null);
    try {
      const r = await api.getStockIssue(id);
      replaceIssue(r.issue);
    } catch { /* ignorat */ }
    void load();
  }

  function openCancel(iss: StockIssue) {
    setCancelNote("");
    setCancelOver([]);
    setCancelling(iss);
  }

  async function submitCancel() {
    if (!cancelling) return;
    setCancelBusy(true);
    setCancelOver([]);
    try {
      const r = await api.cancelStockIssue(cancelling.id, { note: cancelNote.trim() || null });
      setCancelling(null);
      notifications.show({ color: "green", message: t("freeIssues.cancelledToast", { code: r.issue.code }) });
      replaceIssue(r.issue);
      setHoldingsKey((k) => k + 1);
      void load();
    } catch (e) {
      const d = e instanceof ApiError ? (e.detail as { error?: string; items?: unknown[] } | null) : null;
      if (d?.error === "exceeds_holding" && Array.isArray(d.items)) {
        setCancelOver(d.items as OverRow[]);
      } else if (d?.error === "not_editable" && cancelling) {
        notifications.show({ color: "red", message: apiErr(e) });
        void onNotEditable(cancelling.id);
      } else {
        notifications.show({ color: "red", title: t("common.operationFailed"), message: apiErr(e) });
      }
    } finally {
      setCancelBusy(false);
    }
  }

  function apiErr(e: unknown) {
    const code = e instanceof ApiError ? (e.detail as { error?: string } | null)?.error : undefined;
    return code ? t(`freeIssues.errors.${code}`, { defaultValue: code }) : String(e);
  }

  function toggleChecked(id: number, on: boolean) {
    setChecked((c) => {
      const n = new Set(c);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
  }

  useEffect(() => {
    const seq = ++searchSeq.current;
    const h = window.setTimeout(() => {
      api.searchIssueRecipients(recSearch)
        .then((r) => { if (seq === searchSeq.current) setRecOptions(r.recipients); })
        .catch(() => undefined);
    }, 250);
    return () => window.clearTimeout(h);
  }, [recSearch]);

  const recByKey = useMemo(() => {
    const m = new Map<string, IssueRecipient>();
    for (const r of recOptions) m.set(recKey(r), r);
    if (recipient) m.set(recKey(recipient), recipient);
    return m;
  }, [recOptions, recipient]);
  const recData = useMemo(() => [...recByKey.entries()].map(([k, r]) => ({ value: k, label: r.name })), [recByKey]);

  const kindOptions = [
    { value: "issue", label: t("freeIssues.kind.issue") },
    { value: "return", label: t("freeIssues.kind.return") },
    { value: "close", label: t("freeIssues.kind.close") },
  ];

  function handleDone(issue: StockIssue, changes?: StockIssueChange[]) {
    const wasEdit = form?.mode === "edit";
    setForm(null);
    if (wasEdit) {
      notifications.show({
        color: "green",
        title: t("freeIssues.editedToast", { code: issue.code }),
        message: changes && changes.length > 0 ? changesText(changes) : t("freeIssues.editedNoQty"),
      });
      replaceIssue(issue);
      setHoldingsKey((k) => k + 1);
      void load();
      return;
    }
    notifications.show({ color: "green", message: t(`freeIssues.created.${issue.kind}`, { code: issue.code }) });
    setSelected(issue);
    setHoldingsKey((k) => k + 1);
    if (page !== 1) setPage(1);
    else void load();
  }

  const selectBox = (iss: StockIssue) =>
    accPending(iss) ? (
      <Checkbox
        checked={checked.has(iss.id)}
        onChange={(e) => toggleChecked(iss.id, e.currentTarget.checked)}
        aria-label={t("freeIssues.accSelect")}
      />
    ) : null;

  const recipientCell = (iss: StockIssue) => (
    <Group gap={6} wrap="nowrap">
      <Text size="sm">{iss.recipient.name ?? "—"}</Text>
      <RecipientBadge type={iss.recipient.type} />
    </Group>
  );

  return (
    <Container size="lg" px={{ base: "xs", sm: "md" }}>
      <Group justify="space-between" mb="md">
        <Title order={2}>{t("freeIssues.title")}</Title>
        {canEdit && (
          <Group gap="xs">
            <Button
              variant="light"
              color="teal"
              leftSection={<IconArrowBackUp size={16} />}
              onClick={() => setForm({ mode: "return", recipient: null })}
            >
              {t("freeIssues.actionReturn")}
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setForm({ mode: "issue", recipient: null })}>
              {t("freeIssues.newIssue")}
            </Button>
          </Group>
        )}
      </Group>

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="ops">{t("freeIssues.tabOps")}</Tabs.Tab>
          <Tabs.Tab value="holdings">{t("freeIssues.tabHoldings")}</Tabs.Tab>
          <Tabs.Tab value="technicians">{t("freeIssues.tabTechnicians")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="holdings">
          <HoldingsTab
            canEdit={canEdit}
            reloadKey={holdingsKey}
            onAction={(mode, r) => setForm({ mode, recipient: r })}
          />
        </Tabs.Panel>
        <Tabs.Panel value="technicians">
          <TechniciansTab canEdit={canEdit} />
        </Tabs.Panel>
        <Tabs.Panel value="ops">
          <Group mb="sm" gap="xs" wrap="wrap" align="end">
            <Select
              label={t("freeIssues.filterKind")}
              placeholder={t("common.all")}
              data={kindOptions}
              value={kind}
              onChange={(v) => { setKind((v as StockIssueKind) || null); setPage(1); }}
              clearable
              w={{ base: "100%", sm: 180 }}
            />
            <Select
              label={t("freeIssues.filterRecipient")}
              placeholder={t("common.all")}
              data={recData}
              value={recipient ? recKey(recipient) : null}
              onChange={(v) => { setRecipient(v ? recByKey.get(v) ?? null : null); setPage(1); }}
              searchable
              clearable
              searchValue={recSearch}
              onSearchChange={setRecSearch}
              filter={({ options }) => options}
              nothingFoundMessage={t("freeIssues.nothingFound")}
              renderOption={({ option }) => {
                const r = recByKey.get(option.value);
                return (
                  <Group gap="xs" wrap="nowrap" justify="space-between" w="100%">
                    <Text size="sm">{option.label}</Text>
                    {r && <RecipientBadge type={r.type} />}
                  </Group>
                );
              }}
              w={{ base: "100%", sm: 280 }}
            />
            <Switch
              label={t("freeIssues.filterUnconfirmed")}
              checked={unconfirmed}
              onChange={(e) => { setUnconfirmed(e.currentTarget.checked); setPage(1); }}
              mb={8}
            />
            <Switch
              label={t("freeIssues.filterCancelled")}
              checked={cancelledOnly}
              onChange={(e) => { setCancelledOnly(e.currentTarget.checked); setPage(1); }}
              mb={8}
            />
          </Group>

          {canEdit && checked.size > 0 && (
            <Group mb="sm" gap="xs">
              <Button
                size="xs"
                color="green"
                leftSection={<IconCheck size={14} />}
                loading={accBusy}
                onClick={() => void confirmSelected()}
              >
                {t("freeIssues.accConfirmSelected", { count: checked.size })}
              </Button>
              <Button size="xs" variant="subtle" onClick={() => setChecked(new Set())}>
                {t("common.clearSelection")}
              </Button>
            </Group>
          )}

          <ResponsiveTable
            stickyColumns={canEdit ? 2 : 1}
            mobile={{
              rows,
              rowKey: (iss) => iss.id,
              title: (iss) => iss.recipient.name ?? "—",
              subtitle: (iss) => `${iss.code} · ${fmtDate(iss.issued_at)}`,
              badge: (iss) => (
                <Group gap={4} wrap="nowrap">
                  <KindBadge issue={iss} />
                  <StatusBadges issue={iss} />
                </Group>
              ),
              fields: [
                { label: t("freeIssues.colRecipient"), render: (iss) => recipientCell(iss) },
                { label: t("freeIssues.colLines"), render: (iss) => iss.lines.length },
                {
                  label: t("freeIssues.colAccounting"),
                  render: (iss) => (iss.cancelled_at ? <Text size="sm" c="dimmed">—</Text> : <AccountingBadge issue={iss} />),
                },
              ],
              onOpen: (iss) => setSelected(iss),
              selection: canEdit
                ? {
                    isSelected: (iss) => checked.has(iss.id),
                    toggle: (iss) => toggleChecked(iss.id, !checked.has(iss.id)),
                    selectable: (iss) => accPending(iss),
                  }
                : undefined,
              loading: loading && rows.length === 0,
              empty: t("freeIssues.empty"),
            }}
          >
            <Table.ScrollContainer minWidth={700}>
              <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    {canEdit && <Table.Th w={36} />}
                    <Table.Th>{t("freeIssues.colCode")}</Table.Th>
                    <Table.Th>{t("freeIssues.colDate")}</Table.Th>
                    <Table.Th>{t("freeIssues.colKind")}</Table.Th>
                    <Table.Th>{t("freeIssues.colRecipient")}</Table.Th>
                    <Table.Th>{t("freeIssues.colLines")}</Table.Th>
                    <Table.Th>{t("freeIssues.colAccounting")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {!loading && rows.length === 0 && (
                    <Table.Tr><Table.Td colSpan={canEdit ? 7 : 6}><Text c="dimmed">{t("freeIssues.empty")}</Text></Table.Td></Table.Tr>
                  )}
                  {rows.map((iss) => (
                    <Table.Tr key={iss.id} style={{ cursor: "pointer" }} onClick={() => setSelected(iss)}>
                      {canEdit && <Table.Td onClick={(e) => e.stopPropagation()}>{selectBox(iss)}</Table.Td>}
                      <Table.Td><Text size="sm" ff="monospace">{iss.code}</Text></Table.Td>
                      <Table.Td><Text size="sm">{fmtDate(iss.issued_at)}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={4} wrap="nowrap">
                          <KindBadge issue={iss} />
                          <StatusBadges issue={iss} />
                        </Group>
                      </Table.Td>
                      <Table.Td>{recipientCell(iss)}</Table.Td>
                      <Table.Td>{iss.lines.length}</Table.Td>
                      <Table.Td>{iss.cancelled_at ? <Text size="sm" c="dimmed">—</Text> : <AccountingBadge issue={iss} />}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </ResponsiveTable>

          {total > PAGE && (
            <Group justify="center" mt="md">
              <Pagination total={Math.ceil(total / PAGE)} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={form !== null}
        onClose={() => setForm(null)}
        title={form ? t(`freeIssues.formTitle.${form.mode}`, { code: form.issue?.code ?? "" }) : ""}
        size="lg"
        zIndex={300}
        fullScreen={isMobile}
        closeOnEscape={false}
        closeOnClickOutside={false}
      >
        {form && (
          <IssueForm
            key={`${form.mode}:${form.recipient ? `${form.recipient.type}:${form.recipient.id}` : ""}:${form.issue?.id ?? ""}`}
            mode={form.mode}
            initialRecipient={form.recipient}
            issue={form.issue}
            onDone={handleDone}
            onNotEditable={(id) => void onNotEditable(id)}
            onCancel={() => setForm(null)}
          />
        )}
      </Modal>

      <Drawer
        opened={selected !== null}
        onClose={closeDrawer}
        position="right"
        size={isMobile ? "100%" : "md"}
        title={selected ? selected.code : ""}
      >
        {selected && (
          <Stack gap="sm">
            <Group gap="xs">
              <KindBadge issue={selected} />
              <StatusBadges issue={selected} />
              {selected.accounting_required && !selected.cancelled_at && <AccountingBadge issue={selected} />}
            </Group>
            {selected.cancelled_at && (
              <Alert color="red" p="xs">
                <Text size="sm">
                  {t("freeIssues.cancelledInfo", {
                    by: selected.cancelled_by ?? "—", at: fmtDate(selected.cancelled_at),
                  })}
                  {selected.cancel_note ? ` — ${selected.cancel_note}` : ""}
                </Text>
              </Alert>
            )}
            <Paper withBorder p="xs">
              <Stack gap={4}>
                <Group gap={6}>
                  <Text size="sm" c="dimmed">{t("freeIssues.colRecipient")}:</Text>
                  {recipientCell(selected)}
                </Group>
                {selected.target_customer_name && (
                  <Text size="sm"><Text span c="dimmed">{t("freeIssues.targetCustomer")}:</Text> {selected.target_customer_name}</Text>
                )}
                <Text size="sm"><Text span c="dimmed">{t("freeIssues.colDate")}:</Text> {fmtDate(selected.issued_at)}</Text>
                {selected.edited_at && (
                  <Text size="sm"><Text span c="dimmed">{t("freeIssues.editedAt")}:</Text> {fmtDate(selected.edited_at)}</Text>
                )}
                {selected.edited_at && selected.edited_by && (
                  <Text size="sm"><Text span c="dimmed">{t("freeIssues.editedBy")}:</Text> {selected.edited_by}</Text>
                )}
                {selected.act_number && (
                  <Text size="sm"><Text span c="dimmed">{t("freeIssues.actNumber")}:</Text> {selected.act_number}</Text>
                )}
                {selected.accounting_required && selected.accounting_confirmed_at && (
                  <Text size="sm">
                    <Text span c="dimmed">{t("freeIssues.accConfirmedAt")}:</Text> {fmtDate(selected.accounting_confirmed_at)}
                  </Text>
                )}
                {selected.note && (
                  <Text size="sm"><Text span c="dimmed">{t("freeIssues.note")}:</Text> {selected.note}</Text>
                )}
              </Stack>
            </Paper>
            <ResponsiveTable
              mobile={{
                rows: selected.lines,
                rowKey: (ln) => ln.id,
                title: (ln) => ln.stock_item_name ?? `#${ln.stock_item_id}`,
                subtitle: (ln) => `${fmtQty(ln.quantity)} ${ln.unit ?? ""}`,
                fields: [],
                alwaysOpen: true,
              }}
            >
            <Table withTableBorder striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("freeIssues.colItem")}</Table.Th>
                  <Table.Th ta="right">{t("freeIssues.colQty")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selected.lines.map((ln) => (
                  <Table.Tr key={ln.id}>
                    <Table.Td>{ln.stock_item_name ?? `#${ln.stock_item_id}`}</Table.Td>
                    <Table.Td ta="right" style={{ whiteSpace: "nowrap" }}>{fmtQty(ln.quantity)} {ln.unit ?? ""}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </ResponsiveTable>
            {canEdit && editable(selected) && (
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => void openEdit(selected)}
                >
                  {t("freeIssues.editBtn")}
                </Button>
                {user.role === "admin" && (
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    leftSection={<IconBan size={14} />}
                    onClick={() => openCancel(selected)}
                  >
                    {t("freeIssues.cancelBtn")}
                  </Button>
                )}
              </Group>
            )}
            {canEdit && selected.accounting_required && !selected.cancelled_at && (
              <Group gap="xs">
                {selected.accounting_confirmed_at ? (
                  user.role === "admin" && (
                  <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    loading={accBusy}
                    onClick={() => void accountingAction(selected, false)}
                  >
                    {t("freeIssues.accUndo")}
                  </Button>
                  )
                ) : (
                  <Button
                    size="xs"
                    color="green"
                    leftSection={<IconCheck size={14} />}
                    loading={accBusy}
                    onClick={() => void accountingAction(selected, true)}
                  >
                    {t("freeIssues.accConfirm")}
                  </Button>
                )}
              </Group>
            )}
            {selected.kind === "issue" && (
              <Anchor href={api.issueActUrl(selected.id)} target="_blank" rel="noopener">
                <Group gap={4}>
                  <IconFileTypePdf size={18} />
                  {t("freeIssues.actPdf")}
                </Group>
              </Anchor>
            )}
          </Stack>
        )}
      </Drawer>

      <Modal
        opened={cancelling !== null}
        onClose={() => { if (!cancelBusy) setCancelling(null); }}
        title={cancelling ? t("freeIssues.cancelTitle", { code: cancelling.code }) : ""}
        zIndex={300}
        fullScreen={isMobile}
      >
        {cancelling && (
          <Stack>
            <Text size="sm">
              {t("freeIssues.cancelConfirm", { recipient: cancelling.recipient.name ?? "—" })}
            </Text>
            <Textarea
              label={t("freeIssues.cancelNote")}
              value={cancelNote}
              onChange={(e) => setCancelNote(e.currentTarget.value)}
              autosize
              minRows={2}
            />
            {cancelOver.length > 0 && (
              <Alert color="red" title={t("freeIssues.cancelBlocked")}>
                <Stack gap={2}>
                  {cancelOver.map((o) => {
                    const unit = cancelling.lines.find((l) => l.stock_item_id === o.stock_item_id)?.unit ?? "";
                    return (
                      <Text key={o.stock_item_id} size="sm">
                        {o.name}: {t("freeIssues.cancelExceeds", {
                          qty: fmtQty(o.requested), holding: fmtQty(o.holding), unit,
                        })}
                      </Text>
                    );
                  })}
                </Stack>
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCancelling(null)} disabled={cancelBusy}>
                {t("common.back")}
              </Button>
              <Button color="red" loading={cancelBusy} onClick={() => void submitCancel()}>
                {t("freeIssues.cancelSubmit")}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
